import { Injectable, computed, inject } from '@angular/core';
import { CurrentUserService } from './current-user.service';

/**
 * Tells whether the company still has to fill in its objectives page.
 *
 * Drives the warning dots on the "Reduction objectives" menu entry and on the
 * two tabs concerned. Everything is derived from the company already embedded
 * in `GET /me`, so no extra request is needed.
 *
 * The climate risks tab is deliberately left out: it is informative, not
 * something the company commits to.
 */
@Injectable({ providedIn: 'root' })
export class ObjectivesCompletionService {
  private currentUserService = inject(CurrentUserService);

  private company = computed(() => this.currentUserService.currentUser()?.company ?? null);

  /** At least one reduction target switched on with a value ("My reduction objectives" tab). */
  hasReductionTarget = computed(() =>
    this.currentUserService.objectives().some((o) => o.isActive && o.targetValue > 0),
  );

  /**
   * At least one commitment ("My commitments" tab): a reduction action from the
   * catalogue or a free-text one. SDGs are not counted — they are a declaration
   * of intent, not an action the company engages to take.
   */
  hasCommitments = computed(() => {
    const company = this.company();
    return (
      (company?.reductionActions?.length ?? 0) > 0 || (company?.customActions?.length ?? 0) > 0
    );
  });

  /**
   * Warning flags. They stay false until the company is loaded, so the dot
   * never flashes on a page that is still fetching `/me`.
   */
  needsReductionTarget = computed(() => this.company() !== null && !this.hasReductionTarget());
  needsCommitments = computed(() => this.company() !== null && !this.hasCommitments());

  /** Menu-level warning: at least one of the two is still missing. */
  needsAttention = computed(() => this.needsReductionTarget() || this.needsCommitments());
}
